// ============================================================
// Firestore 打卡 task 監聽 adapter 介面（可抽換）
// ------------------------------------------------------------
// 把「對某個 doc 做即時監聽」抽象成介面，讓上層 firestorePunchWatcher 只依賴介面、不綁 Firebase SDK。
//
// 為什麼要這層（bundle 隔離的接縫）：
//   - _app 靜態 import firestorePunchWatcher，而 watcher 只 import 本檔（零 firebase）→ SSR / 主 bundle 不會載到 firebase。
//   - 真正含 firebase 的實作 firestoreFirebaseAdapter，由 _app 在 client 端「動態 import」後 setPunchTaskWatcherAdapter 注入。
//   - 若改讓 watcher 直接 import firebaseAdapter，firebase 會被靜態拉進主 bundle / SSR —— 這層就是為了避免這件事。
//
// 未注入時（SSR / 注入完成前 / 注入失敗）走 no-op stub：安全降級不 crash，dev 端 warn 一次；
// 注入完成後 _app 會呼叫 watcher.resync() 把 stub 期間掛上的監聽全拆重建。
// 整條非同步流程由 punch-in-info 的 use_server_queue 欄位 gate（BE DB 控灰度，免上版）；
// 開關關閉後若 IDB 仍有在途紀錄，_app 會靠 useHasOutstandingAsyncPunch 維持監聽到收尾。
// ============================================================

import { isDev } from '@src/utils/env';
import { PunchTaskDoc } from '@src/utils/punch/punchTaskStatus';

// 一般打卡與卡機打卡的 task 寫在不同 collection（doc key 格式相同，皆為 enqueue 回傳的 document_id）
export const PUNCH_TASK_COLLECTION = 'punch_tasks';
export const PUNCH_MACHINE_TASK_COLLECTION = 'punch_machine_tasks';
export type PunchTaskCollection = typeof PUNCH_TASK_COLLECTION | typeof PUNCH_MACHINE_TASK_COLLECTION;

export const getPunchTaskCollection = (punchKind?: 'regular' | 'machine'): PunchTaskCollection => (
	punchKind === 'machine' ? PUNCH_MACHINE_TASK_COLLECTION : PUNCH_TASK_COLLECTION
);

export interface PunchTaskWatcherAdapter {
	// 監聽 collection/docKey 對應的 document，每次變動呼叫 onUpdate(doc)。回傳 unsubscribe。
	watchDoc(
		docKey: string,
		onUpdate: (doc: PunchTaskDoc | null) => void,
		onError?: (err: unknown) => void,
		collection?: PunchTaskCollection,
	): () => void;
}

let warned = false;
const stubAdapter: PunchTaskWatcherAdapter = {
	watchDoc() {
		if (isDev && !warned) {
			warned = true;
			// eslint-disable-next-line no-console
			console.warn(
				'[punchTaskWatcher] Firestore adapter not injected — async punch results will not be received. ' +
				'Normally injected by _app on client mount via setPunchTaskWatcherAdapter().',
			);
		}
		return () => { /* no-op unsubscribe */ };
	},
};

let currentAdapter: PunchTaskWatcherAdapter = stubAdapter;

export function setPunchTaskWatcherAdapter(adapter: PunchTaskWatcherAdapter): void {
	currentAdapter = adapter;
}

export function watchPunchTaskDoc(
	docKey: string,
	onUpdate: (doc: PunchTaskDoc | null) => void,
	onError?: (err: unknown) => void,
	collection: PunchTaskCollection = PUNCH_TASK_COLLECTION,
): () => void {
	return currentAdapter.watchDoc(docKey, onUpdate, onError, collection);
}
